/**
 * SearchSpring GTM dataLayer Integration
 *
 * Pushes structured events to window.dataLayer for GTM:
 * - ss_results_rendered: when SS results are displayed (with ecommerce impressions)
 * - ss_product_click: when a user clicks a product card (with ecommerce click data)
 * - ss_filter_change: when a filter is applied/removed/cleared
 * - ss_sort_change: when sort order changes
 *
 * Autocomplete GTM events (ss_autocomplete_query, ss_autocomplete_click)
 * are pushed directly by ss-autocomplete.js since it runs on every page
 * while this module only loads on collection/search/recommendation pages.
 *
 * Config options:
 * - listName: display name for GTM events (e.g. "SS Collection: women", "SS Recommendations: similar")
 * - collectionHandle: fallback for listName if not provided
 * - gridSelector: CSS selector for the results grid element (default: '#ss-results-grid')
 * - itemSelector: CSS selector for grid item wrappers (default: '.product-grid__item')
 * - responseGetter: function returning the last API response for product data lookup (optional)
 *
 * Loaded as a defer IIFE — initialized by page-specific orchestrators.
 */

(function () {
  'use strict';

  class SSGtm {
    #listName;
    #gridSelector;
    #itemSelector;
    #responseGetter;

    constructor(config) {
      this.#listName = config.listName || `SS Collection: ${config.collectionHandle || ''}`;
      this.#gridSelector = config.gridSelector || '#ss-results-grid';
      this.#itemSelector = config.itemSelector || '.product-grid__item';
      this.#responseGetter = config.responseGetter || null;
    }

    init() {
      window.dataLayer = window.dataLayer || [];

      document.addEventListener('ss:results-rendered', (e) => {
        this.#onResultsRendered(e.detail?.response);
      });

      const grid = document.querySelector(this.#gridSelector);
      if (grid) {
        grid.addEventListener('click', (e) => this.#onProductClick(e));
      }

      document.addEventListener('ss:filter-changed', (e) => {
        this.#onFilterChanged(e.detail);
      });

      document.addEventListener('ss:sort-changed', (e) => {
        this.#onSortChanged(e.detail);
      });
    }

    #onResultsRendered(response) {
      if (!response) return;

      const products = response.results || [];
      const pagination = response.pagination || {};
      const filterSummary = response.filterSummary || [];
      const sorting = response.sorting || {};

      const currentSort = sorting.options?.find((o) => o.active);

      window.dataLayer.push({
        event: 'ss_results_rendered',
        ss_list: this.#listName,
        ss_result_count: pagination.totalResults || products.length,
        ss_page: pagination.currentPage || 1,
        ss_filters_active: filterSummary.length,
        ss_sort: currentSort ? `${currentSort.field}_${currentSort.direction}` : 'relevance',
        ecommerce: {
          impressions: products.map((p, i) => ({
            id: p.uid || p.id,
            name: p.name || p.title,
            price: p.price,
            position: i + 1,
            list: this.#listName,
          })),
        },
      });
    }

    #onProductClick(e) {
      const card = e.target.closest('product-card');
      if (!card) return;

      const productId = card.getAttribute('data-product-id');
      if (!productId) return;

      const gridItem = card.closest(this.#itemSelector);
      const allItems = Array.from(
        document.querySelectorAll(`${this.#gridSelector} ${this.#itemSelector}`)
      );
      const position = gridItem ? allItems.indexOf(gridItem) + 1 : 0;

      const response = this.#responseGetter
        ? this.#responseGetter()
        : (window.__ssCollection?.lastResponse || window.__ssSearch?.lastResponse);
      const product = response?.results?.find(
        (p) => String(p.uid) === String(productId) || String(p.id) === String(productId)
      );

      window.dataLayer.push({
        event: 'ss_product_click',
        ss_list: this.#listName,
        ecommerce: {
          click: {
            actionField: { list: this.#listName },
            products: [
              {
                id: productId,
                name: product?.name || product?.title || '',
                price: product?.price || '',
                position: position,
              },
            ],
          },
        },
      });
    }

    #onFilterChanged(detail) {
      if (!detail) return;

      window.dataLayer.push({
        event: 'ss_filter_change',
        ss_list: this.#listName,
        ss_filter_field: detail.field || '',
        ss_filter_value: detail.value || '',
        ss_filter_action: detail.action || '',
      });
    }

    #onSortChanged(detail) {
      if (!detail) return;

      window.dataLayer.push({
        event: 'ss_sort_change',
        ss_list: this.#listName,
        ss_sort_field: detail.field || '',
        ss_sort_direction: detail.direction || '',
      });
    }
  }

  window.SSGtm = SSGtm;
})();
